import { CarvanaTheme } from '@carvana/theme';
import React, { useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import ModalContext from '|/app/modals';
import { Summary, Dashboard, Section } from '|/app/services';
import CancelOrderModal from '|/app/steps/navigation-rail/cancel-order-modal';
import EditOrderModal from '|/app/steps/navigation-rail/edit-order-modal';
import * as sc from '|/app/steps/navigation-rail/navigation-rail.styles';
import VehicleSnapshot from '|/app/steps/navigation-rail/vehicle-snapshot';
import routeNameComponentLookup from '|/app/steps/routes.config';
import Modal from '|/components/base/modal';
import theme from '|/components/base/theme';
import SummaryContext from '../../summary-context';

const purchaseEditOrderRouteWhitelist = [
  'identity-verification',
  'bank-account-verification',
  'income-information',
  'residency-information',
  'trade-documentation',
  'additional-income',
  'transfer-schedule'
];

const defaultProps = {
  dashboard: null as null | Dashboard,
  summary: null as null | Summary
};

const optionalProps = {
  onNavigate: () => {}
};

type StatusMapping = { [key in Section]?: string };
const firstSectionStatusMappings: StatusMapping = {
  [1]: 'In progress'
};
const secondSectionStatusMappings: StatusMapping = {
  [1]: '',
  [2]: 'In progress',
  [3]: 'In progress'
};
const defaultStatus = 'Done';

const RootNav = styled.nav`
  min-height: 100vh;
  background-color: var(--colors-neutral-white);

  & > section {
    display: grid;
    grid-template-columns: [first] min-content [second] min-content [third] 1fr;
    grid-template-rows: [header] 56px [nav] 1fr;
    align-items: center;
    border-bottom: 1px solid var(--colors-neutral-gray-2);

    @media (min-width: ${theme.breakpoints.sm}) {
      width: 384px;
    }
  }

  & > section > h2 {
    color: var(--colors-primary-blue-6);
    ${theme.font.label.m};
    text-transform: uppercase;
    margin-left: 30px;
    grid-row: header;
    grid-column: first;
    white-space: nowrap;
  }

  & > section > a {
    margin-left: 30px;
    grid-row: header;
    grid-column: second;
  }

  & > section > p {
    color: var(--colors-neutral-gray-5);
    ${theme.font.body.s}
    white-space: nowrap;
    margin-right: 32px;
    margin-left: auto;
    grid-row: header;
    grid-column: third;
  }

  & nav {
    border-top: 1px solid var(--colors-neutral-gray-2);
    grid-row: nav;
    grid-column: first / span 3;
  }

  & li {
    display: flex;
    border-bottom: 1px solid var(--colors-neutral-gray-2);
    background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M5 14L12 8L5 2" stroke="%23AAB1C7" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg>');
    background-repeat: no-repeat;
    background-position: calc(100% - 32px) center;
  }

  & li.active {
    background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M5 14L12 8L5 2" stroke="%23183558" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg>');
    background-color: var(--colors-neutral-gray-1);
  }

  & li:last-child {
    border-bottom: 0;
  }

  & nav a {
    display: block;
    padding: 30px 32px 30px 30px;
    text-decoration: none;
    width: 100%;
    color: var(--colors-primary-blue-6);
    ${theme.font.header.s}
  }

  & nav a > p {
    margin: 0;
    color: var(--colors-neutral-gray-5);
    ${theme.font.body.s}
  }

  & ol {
    list-style-type: none;
    margin: 0;
    padding: 0;
  }

  & li::before {
    content: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9.5" stroke="%23D9DDE9"/></svg>');
    display: flex;
    align-items: center;
    margin-left: 32px;
  }

  & li.latest::before {
    content: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path opacity="0.3" d="M12.0005 21.5C6.76276 21.5 2.5 17.238 2.5 12C2.5 6.76203 6.76276 2.5 12.0005 2.5C17.2372 2.5 21.5 6.762 21.5 12C21.5 17.238 17.2372 21.5 12.0005 21.5Z" fill="%2300AED9" stroke="%2300AED9"/><path fill-rule="evenodd" clip-rule="evenodd" d="M12.0003 7C9.24333 7 7 9.24292 7 12C7 14.7571 9.24333 17 12.0003 17C14.7567 17 17 14.7571 17 12C17 9.24292 14.7567 7 12.0003 7Z" fill="%2300AED9"/></svg>');
  }

  & li.completed::before {
    content: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M1 10C1 5.02944 5.02944 1 10 1C14.9684 1.00522 18.9948 5.0316 19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10ZM8.88211 14.2632L14.4147 6.99684L14.3958 6.96842C14.6888 6.58386 14.6146 6.03458 14.23 5.74158C13.8454 5.44858 13.2962 5.5228 13.0032 5.90737L8.71158 11.5253L7.16737 9.92421C6.82387 9.58342 6.26981 9.58342 5.92632 9.92421C5.5906 10.2698 5.5906 10.8197 5.92632 11.1653L8.88211 14.2632Z" fill="%232F9E44"/></svg>');
  }

  & li.disabled {
    background-image: none;
  }

  & li.disabled a {
    pointer-events: none;
    color: var(--colors-neutral-gray-3);
  }

  & li.disabled a > p {
    color: var(--colors-neutral-gray-2);
  }
`;

const EditLink = styled.a`
  color: var(--colors-primary-blue-2);
  ${theme.font.link.s}
  cursor: pointer;
`;

const HideEditButtonForNonMobile = styled(EditLink)`
  @media (min-width: ${CarvanaTheme.breakpoints.values.sm}px) {
    display: none;
  }
`;

const NavigationRail: React.FC<
  typeof defaultProps & Partial<typeof optionalProps>
> = ({
  dashboard,
  summary,
  onNavigate = optionalProps.onNavigate
} = defaultProps) => {
  const { steps = [] } = dashboard || {};
  const { vehicle = null, transfer: { deliveryType = null } = {} } =
    summary || {};
  const { pathname } = useLocation();
  const orderedSteps = steps.sort((a, b) => a.order - b.order);
  const latestStep = orderedSteps.find(
    step => step.isEnabled && !step.isComplete
  );

  const [, , , route] = window.location.pathname.split('/');

  const {
    setEditOrderModalOpen,
    setCancelOrderModalOpen,
    isCancelOrderModalShown,
    isEditOrderModalShown
  } = useContext(ModalContext);

  const { setShowSummary } = useContext(SummaryContext);

  if (latestStep && latestStep.section === 3) {
    return null;
  }

  return (
    <RootNav>
      <VehicleSnapshot vehicle={vehicle} />
      <section>
        <h2>Purchase Process</h2>
        {latestStep?.section === 2 && (
          <>
            {purchaseEditOrderRouteWhitelist.includes(route) ? (
              <EditLink
                onClick={() => {
                  onNavigate();
                  setShowSummary(false);
                  setEditOrderModalOpen(true);
                }}
                data-analytics-id="pp_navrail_edit"
              >
                Edit
              </EditLink>
            ) : (
              <HideEditButtonForNonMobile
                onClick={() => {
                  onNavigate();
                  setShowSummary(false);
                  setEditOrderModalOpen(true);
                }}
                data-analytics-id="pp_navrail_edit"
              >
                Edit
              </HideEditButtonForNonMobile>
            )}
          </>
        )}
        <p>
          {latestStep &&
          firstSectionStatusMappings[latestStep.section] !== undefined
            ? firstSectionStatusMappings[latestStep.section]
            : defaultStatus}
        </p>
        {(latestStep === undefined || latestStep.section === 1) && (
          <nav>
            <ol>
              {orderedSteps
                .filter(step => step.section === 1)
                .map(step => {
                  const classes = [];
                  const activeStepOrder = latestStep?.order || 0;
                  if (pathname.startsWith(`/steps/${step.url}`)) {
                    classes.push('active');
                  }
                  if (step.order === activeStepOrder) {
                    classes.push('latest');
                  }
                  if (step.isComplete) {
                    classes.push('completed');
                  }
                  if (step.order > activeStepOrder) {
                    classes.push('disabled');
                  }
                  const completionTimeEstimate =
                    routeNameComponentLookup[step.id]?.completionTimeEstimate ||
                    0;
                  const displayName = step.name;
                  return (
                    step.isEnabled && (
                      <li className={classes.join(' ')} key={step.name}>
                        <Link
                          to={`/steps/${step.url}`}
                          onClick={() => {
                            onNavigate();
                            setShowSummary(false);
                          }}
                          data-analytics-id={`pp_navrail_step_${step.url}`}
                        >
                          {displayName}
                          {step.isComplete ? (
                            <p>Complete</p>
                          ) : (
                            <p>
                              About {completionTimeEstimate} minute
                              {completionTimeEstimate > 1 ? 's' : ''}
                            </p>
                          )}
                        </Link>
                      </li>
                    )
                  );
                })}
            </ol>
          </nav>
        )}
      </section>
      <section>
        <h2>Finalize Purchase</h2>
        <p>
          {latestStep &&
          secondSectionStatusMappings[latestStep.section] !== undefined
            ? secondSectionStatusMappings[latestStep.section]
            : defaultStatus}
        </p>
        {latestStep?.section === 2 && (
          <nav>
            <ol>
              {orderedSteps
                .filter(step => step.section === 2)
                .map(step => {
                  const classes = [];
                  const activeStepOrder = latestStep?.order || 0;
                  if (pathname.startsWith(`/steps/${step.url}`)) {
                    classes.push('active');
                  }
                  if (step.order === activeStepOrder) {
                    classes.push('latest');
                  }
                  if (step.order < activeStepOrder) {
                    classes.push('completed');
                  }
                  if (step.order > activeStepOrder) {
                    classes.push('disabled');
                  }
                  const completionTimeEstimate =
                    routeNameComponentLookup[step.id]?.completionTimeEstimate ||
                    0;
                  const displayName = step.name;
                  return (
                    step.isEnabled && (
                      <li className={classes.join(' ')} key={step.name}>
                        <Link
                          to={`/steps/${step.url}`}
                          onClick={() => {
                            onNavigate();
                            setShowSummary(false);
                          }}
                          data-analytics-id={`pp_navrail_step_${step.url}`}
                        >
                          {displayName}
                          {step.order < activeStepOrder ? (
                            <p>Complete</p>
                          ) : (
                            <p>
                              About {completionTimeEstimate} minute
                              {completionTimeEstimate > 1 ? 's' : ''}
                            </p>
                          )}
                        </Link>
                      </li>
                    )
                  );
                })}
            </ol>
          </nav>
        )}
      </section>
      <sc.CancelAction
        onClick={() => {
          onNavigate();
          setShowSummary(false);
          setCancelOrderModalOpen(true);
        }}
        data-analytics-id="pp_navrail_cancel_link"
      >
        <sc.CancelOrderText>Want to cancel your order?</sc.CancelOrderText>
      </sc.CancelAction>
      {isCancelOrderModalShown && (
        <Modal
          allowBackdropClick={false}
          mobileFullscreen={true}
          onClose={() => setCancelOrderModalOpen(false)}
          showHeaderClose={false}
          title="CANCEL ORDER"
        >
          <CancelOrderModal summary={summary} />
        </Modal>
      )}
      {isEditOrderModalShown && (
        <Modal
          allowBackdropClick={true}
          mobileFullscreen={true}
          onClose={() => setEditOrderModalOpen(false)}
          showHeaderClose={true}
          title="EDIT ORDER"
        >
          <EditOrderModal deliveryPreference={deliveryType} />
        </Modal>
      )}
    </RootNav>
  );
};

export default NavigationRail;
